import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import VideoPlayer from '../components/VideoPlayer';
import ActiveDownloads from '../components/ActiveDownloads';

function formatBytes(bytes) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function Dashboard() {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [url, setUrl] = useState('');
  const [quality, setQuality] = useState('best');
  const [audioOnly, setAudioOnly] = useState(false);
  const [noCompression, setNoCompression] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [storageInfo, setStorageInfo] = useState({ used: 0, limit: 5 * 1024 * 1024 * 1024 });
  const [selectedVideos, setSelectedVideos] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [showPlaylistModal, setShowPlaylistModal] = useState(false);
  const [showCreatePlaylistModal, setShowCreatePlaylistModal] = useState(false);
  const [newPlaylistName, setNewPlaylistName] = useState('');
  const [newPlaylistDescription, setNewPlaylistDescription] = useState('');
  const [playlistFilter, setPlaylistFilter] = useState(null);

  useEffect(() => {
    fetchVideos();
    fetchPlaylists();
    if (user) {
      setStorageInfo({
        used: user.storage_used_bytes || 0,
        limit: user.storage_limit_mb * 1024 * 1024
      });
    }
  }, [user]);

  const fetchPlaylists = async () => {
    try {
      const response = await fetch('/api/playlists', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setPlaylists(data);
      }
    } catch (err) {
      console.error('Failed to fetch playlists:', err);
    }
  };

  const fetchVideos = async () => {
    try {
      const response = await fetch('/api/downloads', {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setVideos(data);
      }
    } catch (err) {
      console.error('Failed to fetch videos:', err);
    }
  };

  const handleDownload = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await fetch('/api/downloads', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          url,
          quality,
          audioOnly,
          noCompression
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Download started');
        setUrl('');
        fetchVideos();
      } else {
        setError(data.error || 'Download failed');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  };

  const handleRedownload = async (id, title) => {
    if (!confirm(`Redownload "${title}" with best quality and no compression? This will replace the existing file.`)) {
      return;
    }

    try {
      const response = await fetch(`/api/downloads/redownload/${id}`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        setSuccess('Redownload started');
        fetchVideos();
      } else {
        const data = await response.json();
        setError(data.error || 'Redownload failed');
      }
    } catch (err) {
      setError('Network error');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this video?')) {
      return;
    }

    try {
      const response = await fetch(`/api/downloads/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        fetchVideos();
      } else {
        setError('Failed to delete');
      }
    } catch (err) {
      setError('Network error');
    }
  };

  const toggleVideoSelect = (id) => {
    setSelectedVideos(prev =>
      prev.includes(id) ? prev.filter(v => v !== id) : [...prev, id]
    );
  };

  const handleAddToPlaylist = async (playlistId) => {
    if (selectedVideos.length === 0) return;

    try {
      const response = await fetch(`/api/playlists/${playlistId}/videos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ videoIds: selectedVideos })
      });

      if (response.ok) {
        setSelectedVideos([]);
        setShowPlaylistModal(false);
        setSuccess('Added to playlist');
        fetchPlaylists();
      }
    } catch (err) {
      setError('Failed to add to playlist');
    }
  };

  const handleCreatePlaylist = async (e) => {
    e.preventDefault();
    if (!newPlaylistName.trim()) return;

    try {
      const response = await fetch('/api/playlists', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: newPlaylistName,
          description: newPlaylistDescription,
          videoIds: selectedVideos.length > 0 ? selectedVideos : undefined
        })
      });

      if (response.ok) {
        setNewPlaylistName('');
        setNewPlaylistDescription('');
        setSelectedVideos([]);
        setShowCreatePlaylistModal(false);
        setSuccess('Playlist created');
        fetchPlaylists();
      }
    } catch (err) {
      setError('Failed to create playlist');
    }
  };

  const handleDownloadFile = async (id, title) => {
    try {
      const response = await fetch(`/api/downloads/download/${id}`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Download failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = title;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download file: ' + err.message);
    }
  };

  const storagePercent = Math.min((storageInfo.used / storageInfo.limit) * 100, 100);

  return (
    <div className="dashboard">
      <nav className="navbar">
        <div className="navbar-brand">
          <h1>MEDIA <span>SERVER</span></h1>
        </div>
        <div className="navbar-right">
          <div className="storage-indicator">
            <span>Storage</span>
            <div className="storage-bar">
              <div 
                className="bar-fill" 
                style={{ width: `${storagePercent}%` }}
              />
            </div>
            <span>{formatBytes(storageInfo.used)} / {formatBytes(storageInfo.limit)}</span>
          </div>
          <span className="user-info">{user?.username}</span>
          <div className="user-actions">
            <button className="btn-secondary" onClick={() => navigate('/playlists')}>
              Playlists {playlists.length > 0 && `(${playlists.length})`}
            </button>
            <button className="btn-secondary" onClick={() => navigate('/gallery')}>
              Gallery
            </button>
            {user?.is_admin && (
              <button className="btn-admin" onClick={() => navigate('/x1n2t3r4n5l')}>
                Admin
              </button>
            )}
            <button className="btn-secondary" onClick={logout}>
              Logout
            </button>
          </div>
        </div>
      </nav>

      <main className="main-content">
        {error && <div className="error-message" style={{ marginBottom: '24px' }}>{error}</div>}
        {success && <div className="success-message" style={{ marginBottom: '24px' }}>{success}</div>}

        <ActiveDownloads />

        <section className="download-section">
          <div className="section-header">
            <h2>Download</h2>
            <span className="number">01</span>
          </div>
          <div className="download-form">
            <form onSubmit={handleDownload}>
              <div className="url-input-group">
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="Paste YouTube URL or Playlist URL"
                  required
                />
                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? 'Processing...' : 'Download'}
                </button>
              </div>
              <div className="form-row">
                <div className="quality-select">
                  <label>Quality</label>
                  <select value={quality} onChange={(e) => setQuality(e.target.value)}>
                    <option value="best">Best Available</option>
                    <option value="1080p">1080p</option>
                    <option value="720p">720p</option>
                    <option value="480p">480p</option>
                  </select>
                </div>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={audioOnly}
                    onChange={(e) => setAudioOnly(e.target.checked)}
                  />
                  Audio only
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={noCompression}
                    onChange={(e) => setNoCompression(e.target.checked)}
                  />
                  No compression
                </label>
              </div>
            </form>
          </div>
        </section>

        {selectedVideos.length > 0 && (
          <section className="playlist-bulk-section">
            <div className="section-header">
              <h2>{selectedVideos.length} Selected</h2>
            </div>
            <div className="bulk-actions">
              <button className="btn-primary" onClick={() => setShowCreatePlaylistModal(true)}>
                + New Playlist
              </button>
              {playlists.length > 0 && (
                <button className="btn-secondary" onClick={() => setShowPlaylistModal(true)}>
                  Add to Playlist
                </button>
              )}
              <button className="btn-secondary" onClick={() => setSelectedVideos([])}>
                Cancel
              </button>
            </div>
          </section>
        )}

        <section className="video-section">
          <div className="section-header">
            <h2>Library</h2>
            <span className="number">02</span>
          </div>
          
          {videos.length === 0 ? (
            <div className="empty-state">
              <h3>No videos</h3>
              <p>Add a YouTube URL to begin</p>
            </div>
          ) : (
            <div className="video-grid">
              {videos.map((video) => (
                <div key={video.id} className={`video-card ${selectedVideos.includes(video.id) ? 'selected' : ''}`}>
                  <div className="video-card-header">
                    <label className="select-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedVideos.includes(video.id)}
                        onChange={() => toggleVideoSelect(video.id)}
                      />
                    </label>
                  </div>
                  <div 
                    className="video-thumbnail"
                    onClick={() => setSelectedVideo(video)}
                  >
                    {video.thumbnail_url ? (
                      <img 
                        src={video.thumbnail_url} 
                        alt={video.title}
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    ) : null}
                    <div className="play-overlay">
                      <div className="play-icon">▶</div>
                    </div>
                  </div>
                  <div className="video-info">
                    <h3>{video.title}</h3>
                    <div className="video-meta">
                      <span>{formatBytes(video.size_bytes)}</span>
                      <span>{video.quality}</span>
                      {video.is_compressed && (
                        <span className="compression-badge">
                          −{Math.round((1 - video.size_bytes / video.original_size_bytes) * 100)}%
                        </span>
                      )}
                      {video.no_compression && (
                        <span className="no-compression-badge">No compression</span>
                      )}
                    </div>
                    <div className="video-actions">
                      <button 
                        className="btn-action"
                        onClick={() => setSelectedVideo(video)}
                      >
                        Play
                      </button>
                      <button 
                        className="btn-action"
                        onClick={() => handleDownloadFile(video.id, video.title)}
                      >
                        Download
                      </button>
                      <button 
                        className="btn-action"
                        onClick={() => handleRedownload(video.id, video.title)}
                      >
                        Redownload
                      </button>
                      <button 
                        className="btn-danger"
                        onClick={() => handleDelete(video.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>

      {selectedVideo && (
        <VideoPlayer 
          video={selectedVideo} 
          onClose={() => setSelectedVideo(null)} 
        />
      )}

      {/* Add to existing playlist modal */}
      {showPlaylistModal && (
        <div className="modal-overlay" onClick={(e) => {
          if (e.target === e.currentTarget) setShowPlaylistModal(false);
        }}>
          <button className="close-btn" onClick={() => setShowPlaylistModal(false)}>×</button>
          <div className="modal-content playlist-modal">
            <h2>Add to Playlist</h2>
            <div className="playlist-list">
              {playlists.map(playlist => (
                <div
                  key={playlist.id}
                  className="playlist-list-item"
                  onClick={() => handleAddToPlaylist(playlist.id)}
                >
                  <span className="playlist-list-name">{playlist.name}</span>
                  <span className="playlist-list-count">{playlist.video_count} videos</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Create new playlist modal */}
      {showCreatePlaylistModal && (
        <div className="modal-overlay" onClick={(e) => {
          if (e.target === e.currentTarget) setShowCreatePlaylistModal(false);
        }}>
          <button className="close-btn" onClick={() => setShowCreatePlaylistModal(false)}>×</button>
          <div className="modal-content create-playlist-modal">
            <h2>Create Playlist</h2>
            <form onSubmit={handleCreatePlaylist}>
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  value={newPlaylistName}
                  onChange={(e) => setNewPlaylistName(e.target.value)}
                  placeholder="My Playlist"
                  required
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>Description (optional)</label>
                <input
                  type="text"
                  value={newPlaylistDescription}
                  onChange={(e) => setNewPlaylistDescription(e.target.value)}
                  placeholder="A description..."
                />
              </div>
              <button type="submit" className="btn-primary">
                Create{selectedVideos.length > 0 ? ` with ${selectedVideos.length} video${selectedVideos.length > 1 ? 's' : ''}` : ''}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;